/**
 * Turning the server's 422 into something a form can point at.
 *
 * django-ninja reports a schema failure as `{detail: [{loc, msg, type}]}`, where
 * the last segment of `loc` is the field it objected to. Browser validation
 * never produces these: the server decides, and this is how it says which input
 * it refused.
 *
 * **Why this is in `lib\` rather than beside a feature.** Four features had
 * each grown their own copy of this walk (providers, OFO, feedback, the
 * wizard), which is the rule of three twice over. The copies had drifted, and
 * the drift was not cosmetic: only one of them stripped the prefix below, so
 * the same refusal read as a sentence in the feedback dialog and as a sentence
 * with pydantic's error class bolted to its front on the provider form. A walk
 * that four screens perform has one home, and the strip goes in that home so a
 * fifth screen inherits it rather than rediscovering it.
 *
 * The feature modules stay as they are named and keep their own semantics,
 * because they genuinely differ (see {@link messageFrom} against
 * {@link formErrorFrom}); they now re-export from here instead of
 * reimplementing.
 *
 * **Where the prefix strip actually lives.** Not here. An audit on 2026-08-05
 * found the wire's message reaching a banner from about fifteen screens that
 * never touch this module, because `client.ts` builds
 * {@link ValidationFailed}'s `message` from the wire's first `msg` and every
 * one of those screens reads `cause.message` directly. So `plainMessage` is
 * defined at that boundary and applied there, which covers all of them plus
 * whatever is written next; this module applies the same function to each
 * field's `msg` on the way past. One definition, applied at each of the two
 * doors a server message can come through.
 */

import { plainMessage, ValidationFailed } from "@/api/client"

export type FieldErrors = Record<string, string>

/**
 * Re-exported so a form can reach the strip without importing the wire client.
 * It is defined at the boundary in `api\client.ts`, where the message it cleans
 * is built; verified against pydantic 2.13.4, the version in `backend\.venv`.
 */
export { plainMessage }

/**
 * Every field the server blamed, mapped to what it said about it.
 *
 * The first message per field wins: a second one about the same input would
 * only push the first off the screen.
 */
export function fieldErrorsFrom(cause: unknown): FieldErrors {
  const errors: FieldErrors = {}
  for (const { field, message } of blamedFields(cause)) {
    if (!errors[field]) errors[field] = message
  }
  return errors
}

/** The server's objection to one named field, or null if it blamed none. */
export function fieldErrorFrom(cause: unknown, field: string): string | null {
  for (const blamed of blamedFields(cause)) {
    if (blamed.field === field) return blamed.message
  }
  return null
}

/**
 * The server's own sentence, or `fallback` when it offered none.
 *
 * Use this where the screen shows the message on its own. Compare
 * {@link formErrorFrom}, which is for the banner above a form whose fields are
 * already carrying their own messages.
 */
export function messageFrom(cause: unknown, fallback: string): string {
  if (cause instanceof Error && cause.message) return plainMessage(cause.message)
  return fallback
}

/**
 * The sentence for the banner above a form.
 *
 * When the server blamed fields, the banner must not repeat one of their
 * messages: the messages are already against the inputs, and a banner quoting
 * the first of them reads as a second, different objection. It points at them
 * instead. When nothing was blamed, there is no highlighted field to point at,
 * so the server's own sentence is all there is.
 */
export function formErrorFrom(cause: unknown, fallback: string): string {
  if (cause instanceof ValidationFailed && Object.keys(fieldErrorsFrom(cause)).length > 0) {
    return "Some details were not accepted. Please check the highlighted fields."
  }
  return messageFrom(cause, fallback)
}

/**
 * The walk itself, in one place: each `{loc, msg}` the server sent, as the
 * field it names and the message a person should read.
 *
 * A `loc` ending in anything but a string is skipped. A list-shaped refusal
 * ends in an index, and no input on any form in this product is named "0", so
 * keying the map by it would put a message on a field nothing renders while
 * still counting towards the total that decides what the banner says.
 */
function* blamedFields(cause: unknown): Generator<{ field: string; message: string }> {
  if (!(cause instanceof ValidationFailed)) return
  const detail = (cause.detail as { detail?: unknown } | null)?.detail
  if (!Array.isArray(detail)) return

  for (const item of detail) {
    const entry = (item ?? {}) as { loc?: unknown; msg?: unknown }
    if (!Array.isArray(entry.loc) || typeof entry.msg !== "string") continue
    const field = entry.loc[entry.loc.length - 1]
    if (typeof field !== "string" || !field) continue
    // An empty message is not an objection anybody can act on, and storing it
    // would mark the field as blamed while showing nothing under it, which also
    // suppresses the banner that would otherwise have said something.
    const message = plainMessage(entry.msg)
    if (!message) continue
    yield { field, message }
  }
}
